import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, Play, Share2, Volume2, VolumeX } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useJoySnaps } from "@/components/insights/useJoySnaps";
import { useYearGarden } from "@/components/insights/useYearGarden";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { buildSlides, drawFrame, SLIDE_MS, SIZE } from "@/components/insights/yearSlides";
import { YearVideoSend } from "@/components/insights/YearVideoSend";
import { playMelody } from "@/lib/melody";

const AC = window.AudioContext || window.webkitAudioContext;

const mimeType = () => ["video/webm;codecs=vp9", "video/webm;codecs=vp8", "video/webm"].find((t) => window.MediaRecorder?.isTypeSupported?.(t));

// Timer-driven (not requestAnimationFrame) so recording keeps going if the tab loses focus.
const animate = (canvas, slides, onEnd) => {
  const ctx = canvas.getContext("2d");
  const total = slides.length * SLIDE_MS;
  const start = performance.now();
  const id = setInterval(() => {
    const t = performance.now() - start;
    drawFrame(ctx, slides, Math.min(t, total - 1));
    if (t >= total) { clearInterval(id); onEnd(); }
  }, 33);
  return () => clearInterval(id);
};

const record = (slides, music) => new Promise((resolve, reject) => {
  const c = document.createElement("canvas");
  c.width = c.height = SIZE;
  const stream = c.captureStream(30);
  let audio = null;
  if (music && AC) {
    audio = new AC();
    const dest = audio.createMediaStreamDestination();
    playMelody(audio, dest, slides.length * SLIDE_MS);
    dest.stream.getAudioTracks().forEach((t) => stream.addTrack(t));
  }
  const rec = new MediaRecorder(stream, { mimeType: mimeType(), videoBitsPerSecond: 2_000_000 });
  const chunks = [];
  rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
  rec.onstop = () => { audio?.close(); resolve(new Blob(chunks, { type: "video/webm" })); };
  rec.onerror = reject;
  rec.start();
  animate(c, slides, () => setTimeout(() => rec.stop(), 200));
});

const slideImages = (slides) => slides.map((_, i) => {
  const c = document.createElement("canvas");
  c.width = c.height = SIZE;
  drawFrame(c.getContext("2d"), slides, i * SLIDE_MS + SLIDE_MS * 0.6);
  return c.toDataURL("image/png");
});

const download = (href, name) => { const a = document.createElement("a"); a.href = href; a.download = name; a.click(); };

export const YearVideo = ({ y, name, onClose }) => {
  const ref = useRef(null);
  const joy = useJoySnaps(y);
  const [garden, gardenNode] = useYearGarden(y);
  const [withJoy, setWithJoy] = useState(true);
  const slides = useMemo(() => buildSlides(y, name, withJoy && joy ? joy : [], garden), [y, name, joy, withJoy, garden]);
  const [playing, setPlaying] = useState(false);
  const [busy, setBusy] = useState("");
  const [music, setMusic] = useState(true);
  const canVideo = !!mimeType() && !!HTMLCanvasElement.prototype.captureStream;

  const setCanvas = (el) => {
    if (el && el !== ref.current) drawFrame(el.getContext("2d"), slides, SLIDE_MS * 0.6);
    ref.current = el;
  };
  useEffect(() => { if (!playing && ref.current) drawFrame(ref.current.getContext("2d"), slides, SLIDE_MS * 0.6); }, [slides, playing]);
  useEffect(() => {
    if (!playing || !ref.current) return undefined;
    const audio = music && AC ? new AC() : null;
    if (audio) playMelody(audio, audio.destination, slides.length * SLIDE_MS);
    const stop = animate(ref.current, slides, () => setPlaying(false));
    return () => { stop(); audio?.close(); };
  }, [playing, slides, music]);

  const file = `mindfulme-year-${y.period}`;
  const saveImages = () => { slideImages(slides).forEach((src, i) => setTimeout(() => download(src, `${file}-${i + 1}.png`), i * 250)); toast.success("Saved the slides as images"); };
  const makeVideo = async () => { setBusy("video"); try { return await record(slides, music); } finally { setBusy(""); } };
  const save = async () => {
    if (!canVideo) return saveImages();
    const blob = await makeVideo();
    const url = URL.createObjectURL(blob);
    download(url, `${file}.webm`);
    setTimeout(() => URL.revokeObjectURL(url), 2000);
    toast.success("Video saved");
  };
  const share = async () => {
    try {
      const f = canVideo ? new File([await makeVideo()], `${file}.webm`, { type: "video/webm" })
        : new File([await (await fetch(slideImages(slides)[0])).blob()], `${file}.png`, { type: "image/png" });
      if (navigator.canShare?.({ files: [f] })) await navigator.share({ files: [f], title: "My year in review" });
      else { await save(); toast.message("Sharing isn't available here, so we saved it instead"); }
    } catch (e) { if (e.name !== "AbortError") toast.error("Could not share"); }
  };

  const btn = "mm-btn-ghost !py-2 !px-3 text-sm inline-flex items-center justify-center gap-1.5 flex-1";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="year-video-dialog">
        <DialogHeader>
          <DialogTitle>Your year, in {Math.round((slides.length * SLIDE_MS) / 1000)} seconds</DialogTitle>
          <DialogDescription>{canVideo ? "Play it here, then save or share it as a video." : "Your browser can't save video, so saving gives you the slides as images."}</DialogDescription>
        </DialogHeader>
        <canvas ref={setCanvas} width={SIZE} height={SIZE} className="w-full rounded-2xl border" data-testid="year-video-canvas" />
        <div className="flex gap-2">
          <button className={btn} disabled={playing || !!busy} onClick={() => setPlaying(true)} data-testid="year-video-play"><Play size={15} /> {playing ? "Playing…" : "Play"}</button>
          <button className={btn} disabled={!!busy} onClick={save} data-testid="year-video-save">{busy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} {busy ? "Making…" : "Save"}</button>
          <button className={btn} disabled={!!busy} onClick={share} data-testid="year-video-share"><Share2 size={15} /> Share</button>
        </div>
        <button className="text-sm text-mm-muted inline-flex items-center gap-1.5 self-start" onClick={() => { setPlaying(false); setMusic((m) => !m); }} data-testid="year-video-music" aria-pressed={music}>
          {music ? <Volume2 size={15} /> : <VolumeX size={15} />} Music {music ? "on" : "off"}
        </button>
        {joy?.length > 0 && (
          <label className="flex items-center gap-2 text-sm" data-testid="year-video-joy-toggle-label">
            <Switch checked={withJoy} disabled={playing || !!busy} onCheckedChange={setWithJoy} data-testid="year-video-joy-toggle" />
            Include my Joy Snaps ({joy.length} month{joy.length === 1 ? "" : "s"})
          </label>
        )}
        {joy === null && <p className="text-xs text-mm-muted inline-flex items-center gap-1.5" data-testid="year-video-joy-loading"><Loader2 size={12} className="animate-spin" /> Loading your Joy Snaps…</p>}
        {canVideo && <YearVideoSend y={y} makeVideo={makeVideo} busy={!!busy} />}
        {gardenNode}
      </DialogContent>
    </Dialog>
  );
};
